import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield, Filter, RefreshCw } from "lucide-react";

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ actor_id: "", action: "all", entreprise_id: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (filters.actor_id) params.set("actor_id", filters.actor_id);
      if (filters.action && filters.action !== "all") params.set("action", filters.action);
      if (filters.entreprise_id) params.set("entreprise_id", filters.entreprise_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const [r, a] = await Promise.all([
        api.get(`/admin/audit-logs?${params.toString()}`),
        api.get("/admin/audit-logs/actions"),
      ]);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setActions(a.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const COLORS = {
    "entreprise.create": "#059669",
    "entreprise.activate": "#059669",
    "entreprise.deactivate": "#D97706",
    "entreprise.block": "#E60000",
    "salarie.create": "#059669",
    "conge.request": "#0EA5E9",
    "conge.approved": "#059669",
    "conge.rejected": "#E60000",
    "absence.signal": "#D97706",
    "chef.bulletin.upload": "#002FA7",
    "chef.document.upload": "#002FA7",
    "salarie.document.upload": "#0EA5E9",
    "chef.notification.send": "#868E96",
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Admin · Audit RGPD"
        title="Journal d'audit"
        sub="Trace anonymisée des actions sensibles — qui a fait quoi, quand. Aucun mot de passe ni IBAN n'est enregistré."
        right={
          <Button onClick={load} disabled={loading} data-testid="audit-reload-btn" className="h-11 bg-[#002FA7] text-white">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        <section className="bg-white border border-[#DEE2E6] p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="audit-filters">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Action</Label>
            <Select value={filters.action} onValueChange={(v) => setF("action", v)}>
              <SelectTrigger className="h-11 mt-1" data-testid="audit-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Acteur ID</Label>
            <Input value={filters.actor_id} onChange={(e) => setF("actor_id", e.target.value)} className="h-11 mt-1" placeholder="uuid acteur" data-testid="audit-actor" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Entreprise ID</Label>
            <Input value={filters.entreprise_id} onChange={(e) => setF("entreprise_id", e.target.value)} className="h-11 mt-1" placeholder="uuid entreprise" data-testid="audit-ent" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Du</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="h-11 mt-1" data-testid="audit-from" />
          </div>
          <div className="flex gap-2">
            <Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="h-11 flex-1" data-testid="audit-to" placeholder="Au" />
            <Button onClick={load} className="h-11 bg-[#002FA7] text-white" data-testid="audit-apply"><Filter className="h-4 w-4" /></Button>
          </div>
        </section>

        <section className="bg-white border border-[#DEE2E6]" data-testid="audit-list">
          <header className="p-5 border-b border-[#DEE2E6] text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#002FA7]" /> {items.length} entrée(s) affichées · {total} total
          </header>
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">Aucune entrée correspondante.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F8F9FA] text-left text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="p-3">Date</th>
                    <th className="p-3">Acteur</th>
                    <th className="p-3">Rôle</th>
                    <th className="p-3">Action</th>
                    <th className="p-3">Cible</th>
                    <th className="p-3">Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-[#F8F9FA] hover:bg-[#F8F9FA]" data-testid={`audit-row-${it.id}`}>
                      <td className="p-3 font-mono text-[10px] whitespace-nowrap">{new Date(it.created_at).toLocaleString("fr-FR")}</td>
                      <td className="p-3"><div className="font-bold">{it.actor_name || "—"}</div><div className="font-mono text-[9px] text-[#868E96]">{it.actor_code}</div></td>
                      <td className="p-3"><span className="px-2 py-0.5 bg-[#F8F9FA] border border-[#DEE2E6] text-[10px] uppercase">{it.actor_role}</span></td>
                      <td className="p-3"><span className="font-mono font-bold" style={{ color: COLORS[it.action] || "#0A0A0A" }}>{it.action}</span></td>
                      <td className="p-3 font-mono text-[10px]">{it.target_type || "—"}{it.target_id ? `:${it.target_id.slice(0, 8)}…` : ""}</td>
                      <td className="p-3 font-mono text-[10px] max-w-md truncate">{Object.keys(it.meta || {}).length ? JSON.stringify(it.meta) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
